/**
 * Plan Management Page - Table-based layout for managing subscription plans
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Plus, Search, CreditCard, Edit2, Trash2, Layers, Shield, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import planService, { Plan } from '../../services/api/planService';
import { CreatePlanModal } from './CreatePlanModal';
import { EditPlanModal } from './EditPlanModal';
import { EditPlanModulesModal } from './EditPlanModulesModal';
import { EditPlanPermissionsModal } from './EditPlanPermissionsModal';
import { EditPricingTiersModal } from './EditPricingTiersModal';
import { ConfirmationDialog } from './ConfirmationDialog';

export const PlanManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editingModulesPlan, setEditingModulesPlan] = useState<Plan | null>(null);
  const [editingPermissionsPlan, setEditingPermissionsPlan] = useState<Plan | null>(null);
  const [editingTiersPlan, setEditingTiersPlan] = useState<Plan | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    plan: Plan | null;
  }>({ isOpen: false, plan: null });

  const queryClient = useQueryClient();

  const { data: plansData, isLoading, isError } = useQuery({
    queryKey: ['plans', searchQuery],
    queryFn: () => planService.getPlans({ search: searchQuery, limit: 100 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => planService.deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      setConfirmationDialog({ isOpen: false, plan: null });
    },
  });

  const handleConfirmDelete = () => {
    if (confirmationDialog.plan) {
      deleteMutation.mutate(confirmationDialog.plan.id);
    }
  };

  const plans = plansData?.data ?? [];

  return (
    <Container>
      <Header>
        <Title>Plans</Title>
        <HeaderActions>
          <SearchContainer>
            <Input
              type="search"
              placeholder="Search plans..."
              leftIcon={<Search />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
            />
          </SearchContainer>
          <Button
            variant="primary-blue"
            leftIcon={<Plus />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Create Plan
          </Button>
        </HeaderActions>
      </Header>

      {isLoading ? (
        <EmptyState>Loading plans...</EmptyState>
      ) : isError ? (
        <ErrorState>Failed to load plans. Please try again later.</ErrorState>
      ) : (
        <Section>
          <SectionHeader>
            <CreditCard size={18} />
            <SectionTitle>Subscription Plans</SectionTitle>
            <SectionCount>{plans.length}</SectionCount>
          </SectionHeader>

          {plans.length === 0 ? (
            <EmptyState>
              No plans yet. Create your first subscription plan.
            </EmptyState>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Description</Th>
                    <Th $center>Status</Th>
                    <Th $right>Actions</Th>
                    <Th $right>Modules</Th>
                    <Th $right>Permissions</Th>
                    <Th $right>Tiers</Th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => (
                    <Tr key={plan.id}>
                      <Td>
                        <PlanCell>
                          <PlanName>{plan.name}</PlanName>
                          {plan.organizationCount !== undefined && (
                            <PlanMeta>{plan.organizationCount} org{plan.organizationCount !== 1 ? 's' : ''}</PlanMeta>
                          )}
                        </PlanCell>
                      </Td>
                      <Td>
                        <Description>{plan.description || <EmptyDash>&mdash;</EmptyDash>}</Description>
                      </Td>
                      <Td $center>
                        <Badge variant={plan.isActive ? 'success' : 'neutral'}>
                          {plan.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td $right>
                        <ActionsRow>
                          <ActionLink onClick={() => setEditingPlan(plan)}>
                            <Edit2 size={14} />
                            Edit
                          </ActionLink>
                          <ActionLinkDanger
                            onClick={() => setConfirmationDialog({ isOpen: true, plan })}
                          >
                            <Trash2 size={14} />
                            Delete
                          </ActionLinkDanger>
                        </ActionsRow>
                      </Td>
                      <Td $right>
                        <ActionLink onClick={() => setEditingModulesPlan(plan)}>
                          <Layers size={14} />
                          Modules
                        </ActionLink>
                      </Td>
                      <Td $right>
                        <ActionLink onClick={() => setEditingPermissionsPlan(plan)}>
                          <Shield size={14} />
                          Permissions
                        </ActionLink>
                      </Td>
                      <Td $right>
                        <ActionLink onClick={() => setEditingTiersPlan(plan)}>
                          <DollarSign size={14} />
                          Tiers
                        </ActionLink>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </Section>
      )}

      <CreatePlanModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      {editingPlan && (
        <EditPlanModal
          isOpen
          onClose={() => setEditingPlan(null)}
          plan={editingPlan}
        />
      )}

      {editingModulesPlan && (
        <EditPlanModulesModal
          isOpen
          onClose={() => setEditingModulesPlan(null)}
          plan={editingModulesPlan}
        />
      )}

      {editingPermissionsPlan && (
        <EditPlanPermissionsModal
          isOpen
          onClose={() => setEditingPermissionsPlan(null)}
          plan={editingPermissionsPlan}
        />
      )}

      {editingTiersPlan && (
        <EditPricingTiersModal
          isOpen
          onClose={() => setEditingTiersPlan(null)}
          plan={editingTiersPlan}
        />
      )}

      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={() => setConfirmationDialog({ isOpen: false, plan: null })}
        onConfirm={handleConfirmDelete}
        plan={confirmationDialog.plan}
        isLoading={deleteMutation.isPending}
      />
    </Container>
  );
};

export default PlanManagementPage;

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    flex-direction: column;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const SectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const Th = styled.th<{ $center?: boolean; $right?: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: ${({ $center, $right }) => ($right ? 'right' : $center ? 'center' : 'left')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light}40;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray}60;
  }
`;

const Td = styled.td<{ $center?: boolean; $right?: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  text-align: ${({ $center, $right }) => ($right ? 'right' : $center ? 'center' : 'left')};
  vertical-align: middle;
`;

const PlanCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PlanName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PlanMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
`;

const Description = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyDash = styled.span`
  color: ${({ theme }) => theme.colors.text.light};
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ActionLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary.base};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const ActionLinkDanger = styled(ActionLink)`
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ErrorState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.status.error.text};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
